import React, { useContext } from 'react';
import LoadingSpinner from './shared/LoadingSpinner';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { hasTradePlatformAccess } from '../utils/authAccess';
import { needsSubscriptionPaywall, shouldRedirectToOnboarding } from '../utils/onboardingStatus';
import { hasConnectTradeCallbackQuery } from '../utils/connectTradeCallbackErrors';
import { useTradingRulesConfig } from '../contexts/TradingRulesConfigContext';
import TrialExpiredModal from './billing/TrialExpiredModal';
import BackendOutageCard from './BackendOutageCard';

const getCachedAuthToken = () => {
    try {
        return localStorage.getItem('authToken');
    } catch {
        return null;
    }
};

const RequireAuthAndEarlyAccess = ({ children }) => {
    const { user, isInitializing, backendDown } = useContext(AuthContext);
    const location = useLocation();
    const { settings: rulesSettings } = useTradingRulesConfig();
    const cachedAuthToken = getCachedAuthToken();

    if (isInitializing) {
        return (
            <div style={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--bg-primary, #0f172a)',
                zIndex: 2000
            }}>
                <LoadingSpinner />
            </div>
        );
    }

    // Fully logged out — never show outage UI (stale backendDown after invalid session)
    if (!user?.token && !cachedAuthToken) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    if (backendDown && (user?.token || cachedAuthToken)) {
        return <BackendOutageCard />;
    }

    if (!user?.token) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    if (!hasTradePlatformAccess(user)) {
        // Trial expired / payment failed — show children underneath a blocking modal
        if (needsSubscriptionPaywall(user)) {
            return (
                <>
                    {children}
                    <TrialExpiredModal show />
                </>
            );
        }

        // New user, no trial started → funnel through onboarding
        return <Navigate to="/onboarding" replace state={{ from: location }} />;
    }

    if (shouldRedirectToOnboarding(user, rulesSettings) && !hasConnectTradeCallbackQuery(location.search)) {
        return <Navigate to="/onboarding" replace state={{ from: location }} />;
    }

    return children;
};

export default RequireAuthAndEarlyAccess;
